/* eslint-disable @typescript-eslint/no-explicit-any */
import { InputXIcon } from "@src/assets/icons/InputXIcon";
import React, { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
	CustomInput,
	InputContainer,
	InputIcons,
	InputPrefix,
	LabelContainer,
	MainContainer,
	StatusContainer,
	XIconContainer,
} from "./Input.styled";
import { Control, useController } from "react-hook-form";
import { cleanNumber, formatCurrency } from "@src/utils/numberUtils";

export interface InputProps {
	control: Control<any>;
	name: string;
	label?: string;
	controlSize?: "n" | "sm" | "xsm";
	currency?: boolean;
	wrapperStyle?: CSSProperties;
	wrapperClassName?: string;
	inputContainerClassName?: string;
	style?: CSSProperties;
	className?: string;
	disableErrorMessage?: boolean;
	value?: string | number;
	disableBorder?: boolean;
	disableXButton?: boolean;
	type?: "text" | "number" | "checkbox" | "password" | "date";
	placeholder?: string;
	disabled?: boolean;
	inputPrefix?: React.ReactNode;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	onFocus?: React.FocusEventHandler<HTMLInputElement>;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
	step?: string | number;
}

export interface InputRef {
	focus: () => void;
	select: () => void;
}

const InputInner = (
	{
		control,
		name,
		label,
		controlSize = "n",
		currency,
		wrapperStyle,
		style,
		disableErrorMessage,
		disableBorder = false,
		disableXButton = false,
		type,
		placeholder,
		disabled,
		inputPrefix,
		onFocus,
		onKeyDown,
		onBlur,
		onChange,
		step,
	}: InputProps,
	ref: React.Ref<InputRef>,
): React.ReactElement => {
	const inputRef = useRef<HTMLInputElement>(null);
	const { field, fieldState } = useController({ name, control });

	useImperativeHandle(ref, () => ({
		focus: () => {
			inputRef.current?.focus();
		},
		select: () => {
			inputRef.current?.select();
		},
	}));

	const [displayValue, setDisplayValue] = useState(`${field.value}`);

	const errorMessage = fieldState.error?.message;
	const { isDirty, invalid } = fieldState;

	const innerTernary: "none" | "success" = field.value && isDirty ? "success" : "none";
	const inputStatus: "none" | "success" | "error" = invalid ? "error" : innerTernary;

	useEffect(() => {
		if (!currency) return;
		setDisplayValue(formatCurrency(field.value as number));
	}, [currency, field.value]);

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		if (currency) {
			const number = cleanNumber(e.target.value);
			const formattedValue = formatCurrency(number);

			setDisplayValue(formattedValue);
			field.onChange(number);
		}
		field.onBlur();
		onBlur?.(e);
	};
	const requiredProps: React.ComponentProps<"input"> = {
		ref: control
			? (el: HTMLInputElement) => {
					field.ref(el);
				}
			: inputRef,
		onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (!currency) return true;
			const target = e.target as HTMLInputElement;

			if (
				!"0123456789.$".includes(e.key) ||
				(e.key === "$" && target.value.includes("$")) ||
				(e.key === "." && target.value.includes("."))
			) {
				e.preventDefault();
				return false;
			}
		},
		onBlur: handleBlur,
		onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e);
			if (currency) setDisplayValue(e.target.value);
			else if (type == "checkbox") field.onChange(e.target.checked);
			else field.onChange(e.target.value);
		},
		onKeyDown: onKeyDown,
		name: field.name,
		checked: typeof field.value == "boolean" ? field.value : false,
		value: currency ? displayValue : field.value,
		autoComplete: "off",
		placeholder: placeholder,
		type: type,
		style: style,
		disabled: disabled,
		step: step,
	};

	const resetFieldValue = () => {
		field.onChange("");
	};

	const showIconsContainer = !disableXButton && String(field.value);

	return (
		<MainContainer style={wrapperStyle}>
			{label && <LabelContainer $isDisabled={disabled}>{label}</LabelContainer>}
			<InputContainer $disableBorder={disableBorder} $inputStatus={inputStatus}>
				{inputPrefix ? <InputPrefix $isDisabled={disabled}>{inputPrefix}</InputPrefix> : null}
				<CustomInput onFocus={onFocus} $controlSize={controlSize} $inputType={type} {...requiredProps} />
				{showIconsContainer ? (
					<InputIcons $isDisabled={disabled}>
						{disableXButton ? null : (
							<XIconContainer aria-hidden="true" onClick={resetFieldValue}>
								<InputXIcon />
							</XIconContainer>
						)}
					</InputIcons>
				) : null}
			</InputContainer>
			{!disableErrorMessage && errorMessage && <StatusContainer $inputStatus={inputStatus}>{errorMessage}</StatusContainer>}
		</MainContainer>
	);
};

export const Input = forwardRef<InputRef, InputProps>(InputInner);
